import React, { Dispatch, SetStateAction } from "react";
import { ChoiceGroup, ContextualMenu, IChoiceGroupOption, MessageBar, MessageBarType } from "@fluentui/react";
import { DimensionDetails, SheetForm, TgkAmountObjectSource } from "../../interfaces/CoObject";
import { ObjectPanelTabs } from "../co-panel/ObjectPanelComponent";
import { ActionButton } from "@fluentui/react/lib/Button";
import { TreeDropDownInput } from "../tree-pills-input/TreeInput";
import { fetchChildrenDimension, mapDimensionToDimensionItem } from "../../../api/object/Dimensions";
import { formatMessage, formatMessageWithParams } from "../../../utils/helpers/LanguageHelper";
import { DbApplicationDropDown } from "../DbApplicationDropdown";
import { SourceTypeEnum } from "../../../api/models/source-type-enum";
import { ExcelAmountSourcePanel } from "../co-amount-excel-panel/ExcelAmountSourcePanel";
import useCoObjectSourceAmountPanel from "./CoObjectSourceAmountPanelHook";
import { TgkFormAmountSourcePanel } from "../co-amount-tgk-form-panel/TgkFormAmountSourcePanel";
import { AnalyticalAmountSourcePanel } from "../co-amount-analytical-panel/AnalyticalAmountSourcePanel";
import { DatasetField } from "../../../api/models/dataset-field";
import { isCpmVersionLess } from "../../../utils/helpers/ApiResolver";
import { selectExternalConfigs } from "../../../redux/feature/settings/configurations-slice";
import { store } from "../../../redux/store/store";

interface CoObjectSourceAmountProps {
  objectSource: TgkAmountObjectSource;
  updateCoObjectSourceAmount: (obj: TgkAmountObjectSource, tabValid, modified: boolean) => void;
  isEdit: boolean;
  isLocked: boolean;
  isComponentInstance: boolean;
  tab: ObjectPanelTabs;
  inheritableParameters: DimensionDetails[] | undefined;
  isFileExcelExisting?: boolean;
  fileExcelName?: string;
  updateFileExcelName?: (fileExcelName: string) => void;
  datasetFields: DatasetField[];
  originalSheetArea: SheetForm;
  setOriginalSheetArea?: Dispatch<SetStateAction<SheetForm>>;
  setIsOldSheetOrAreaSelected?: Dispatch<SetStateAction<boolean>>;
  setIsLoadingSheetAndArea?: Dispatch<SetStateAction<boolean>>;
  setIsSheetChartAndAreaToHide?: Dispatch<SetStateAction<boolean>>;
  isSheetChartAndAreaToHide?: boolean;
}

export const CoObjectSourceAmountPanel: React.FunctionComponent<CoObjectSourceAmountProps> = (props) => {
  const {
    objectSource,
    isEdit,
    isLocked,
    isComponentInstance,
    datasetFields,
    amountSourceTypeEnumOptions,
    isExcelSource,
    validateExcelSource,
    isTgkSource,
    isTgkFormSource,
    validateTgkFormSource,
    validateFields,
    validateAnalytical,
    onChangeFormattingAs,
    updateCoObjectSourceAmount,
    selectDBAndFetchDefaultRoots,
    mandatoryDimensionsItems,
    inheritableParameters,
    updateDimensionFromTree,
    userSelectedDimensionsItems,
    removeDimension,
    filteredDimensionsRoot,
    linkRef,
    addDimension,
    setUserSelectedDimensionsState,
    isContextualMenuOpen,
    isFileExcelExisting,
    fileExcelName,
    updateFileExcelName,
  } = useCoObjectSourceAmountPanel(props);

  const isOldVersion = isCpmVersionLess("5.3.32.0");

  const amountCCHTagetikTypeOptions: IChoiceGroupOption[] = [
    {
      ariaLabel: "tagetik_source_checkbox",
      key: "AMOUNT",
      text: formatMessage("cof.base.financial"),
    },
    {
      ariaLabel: "tagetik_form_source_checkbox",
      key: "AMOUNT_AIH",
      text: formatMessage("cof.base.analytical"),
      disabled: isOldVersion,
    },
  ];

  function onChangeAmountType(amountType: "AMOUNT" | "AMOUNT_AIH") {
    const objSource: TgkAmountObjectSource = {
      ...objectSource,
      type: amountType,
      dimensions: [],
    };
    updateCoObjectSourceAmount(objSource, validateFields(objSource, mandatoryDimensionsItems), true);
  }

  return (
    <>
      <div className="ms-Grid" dir="ltr">
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <ChoiceGroup
              disabled={isEdit || isComponentInstance || isLocked}
              selectedKey={
                objectSource?.objectSource && objectSource.objectSource.length > 0
                  ? objectSource?.objectSource
                  : SourceTypeEnum.TAGETIK
              }
              defaultSelectedKey={objectSource?.objectSource ? objectSource?.objectSource : SourceTypeEnum.TAGETIK}
              styles={{
                flexContainer: { display: "flex", justifyContent: "left", columnGap: "0.5rem", flexWrap: "wrap" },
              }}
              options={amountSourceTypeEnumOptions}
              onChange={onChangeFormattingAs}
            />
          </div>

          {isTgkSource() && (
            <>
              <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                <DbApplicationDropDown
                  disabled={isComponentInstance || isLocked}
                  dbApplication={objectSource?.dbApplication}
                  selectDbComboBox={selectDBAndFetchDefaultRoots}
                />
                <ChoiceGroup
                  disabled={isEdit || isComponentInstance || isLocked}
                  selectedKey={objectSource.type}
                  styles={{
                    flexContainer: { display: "flex", justifyContent: "left", columnGap: "0.5rem", flexWrap: "wrap" },
                  }}
                  options={amountCCHTagetikTypeOptions}
                  onChange={(_event, option) => onChangeAmountType(option.key as "AMOUNT" | "AMOUNT_AIH")}
                />
                {isOldVersion && (
                  <MessageBar messageBarType={MessageBarType.warning}>
                    {formatMessageWithParams("cof.newobject.amount.notavailable", {
                      cpmVersion: selectExternalConfigs(store.getState()).cpmVersion,
                    })}
                  </MessageBar>
                )}
              </div>
              {objectSource?.dbApplication?.id &&
                objectSource?.type === "AMOUNT" &&
                mandatoryDimensionsItems.map((dimensionItem) => (
                  <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12" key={dimensionItem.oid + "DropDown"}>
                    <TreeDropDownInput
                      fetchRoot={async (oid) => {
                        const items = await fetchChildrenDimension(objectSource?.dbApplication?.id, oid);
                        return items.map((i) => mapDimensionToDimensionItem(i));
                      }}
                      fetchChildren={async (item) => {
                        const items = await fetchChildrenDimension(objectSource?.dbApplication?.id, item.data.oid);
                        return items.map((i) => mapDimensionToDimensionItem(i));
                      }}
                      dimensions={objectSource?.dimensions?.filter((d) => d.dimensionOid === dimensionItem.oid)}
                      label={dimensionItem.desc + " *"}
                      modalTitle={dimensionItem.desc}
                      placeholder={formatMessage("cof.base.select")}
                      rootOid={dimensionItem.oid}
                      hierarchical={dimensionItem.hierarchical}
                      canInherit={inheritableParameters?.filter((d) => d.dimensionOid === dimensionItem.oid) ?? []}
                      inherited={objectSource?.dimensions?.find((d) => d.dimensionOid === dimensionItem.oid)?.inherited}
                      multiSelectable={dimensionItem.multiSelectable}
                      onChange={(newDimension) => {
                        // eslint-disable-next-line no-undef
                        updateDimensionFromTree(dimensionItem, newDimension);
                      }}
                      dbId={objectSource?.dbApplication?.id}
                      disabled={isComponentInstance || isLocked}
                      isSearchAvailable={true}
                    />
                  </div>
                ))}
              {objectSource?.dbApplication?.id &&
                objectSource?.type === "AMOUNT" &&
                userSelectedDimensionsItems.map((dimensionItem, index, arr) => (
                  <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12" key={dimensionItem.oid + "DropDown"}>
                    <TreeDropDownInput
                      fetchRoot={async (oid) => {
                        const items = await fetchChildrenDimension(objectSource?.dbApplication?.id, oid);
                        return items.map((i) => mapDimensionToDimensionItem(i));
                      }}
                      fetchChildren={async (item) => {
                        const items = await fetchChildrenDimension(objectSource?.dbApplication?.id, item.data.oid);
                        return items.map((i) => mapDimensionToDimensionItem(i));
                      }}
                      dimensions={objectSource?.dimensions?.filter((d) => d.dimensionOid === dimensionItem.oid)}
                      label={dimensionItem.desc}
                      modalTitle={dimensionItem.desc}
                      placeholder={formatMessage("cof.base.select")}
                      rootOid={dimensionItem.oid}
                      canInherit={inheritableParameters?.filter((d) => d.dimensionOid === dimensionItem.oid) ?? []}
                      inherited={objectSource?.dimensions?.find((d) => d.dimensionOid === dimensionItem.oid)?.inherited}
                      hierarchical={dimensionItem.hierarchical}
                      multiSelectable={dimensionItem.multiSelectable}
                      onChange={(newDimension) => {
                        updateDimensionFromTree(dimensionItem, newDimension);
                      }}
                      onRemove={
                        !isComponentInstance &&
                        (() => {
                          removeDimension(dimensionItem.oid);
                          arr.splice(index, 1);
                          setUserSelectedDimensionsState((s) => ({
                            ...s,
                            userSelectedDimensionsItems: arr,
                            isContextualMenuOpen: false,
                          }));
                        })
                      }
                      dbId={objectSource?.dbApplication?.id}
                      disabled={isComponentInstance || isLocked}
                      isSearchAvailable={true}
                    />
                  </div>
                ))}
              {objectSource?.dbApplication?.id && objectSource?.type === "AMOUNT" && !isComponentInstance && (
                <ActionButton
                  iconProps={{ iconName: "CircleAdditionSolid" }}
                  disabled={!filteredDimensionsRoot.length || isLocked}
                  onClick={() => {
                    setUserSelectedDimensionsState((s) => ({
                      ...s,
                      isContextualMenuOpen: true,
                    }));
                  }}
                  style={{
                    float: "left",
                    marginBottom: "5px",
                  }}
                >
                  <b ref={linkRef} style={{ color: filteredDimensionsRoot.length ? "var(--color-blue)" : "inherit" }}>
                    {formatMessage("cof.base.adddimension")}
                  </b>
                </ActionButton>
              )}
              {objectSource?.dbApplication?.id && objectSource?.type === "AMOUNT_AIH" && (
                <AnalyticalAmountSourcePanel
                  objectSource={objectSource}
                  isComponentInstance={isComponentInstance}
                  isLocked={isLocked}
                  updateSource={updateCoObjectSourceAmount}
                  datasetFields={datasetFields}
                  isEdit={isEdit}
                  validateSource={validateAnalytical}
                />
              )}
              {isContextualMenuOpen && (
                <ContextualMenu
                  target={linkRef}
                  items={filteredDimensionsRoot.map((d) => ({
                    key: d.oid,
                    text: d.desc,
                    onClick: () => {
                      addDimension(d);
                    },
                  }))}
                  onDismiss={() => {
                    setUserSelectedDimensionsState((s) => ({
                      ...s,
                      isContextualMenuOpen: false,
                    }));
                  }}
                />
              )}
            </>
          )}

          {isTgkFormSource() && (
            <TgkFormAmountSourcePanel
              isEdit={isEdit}
              objectSource={objectSource}
              updateSource={updateCoObjectSourceAmount}
              validateSource={validateTgkFormSource}
              selectDBAndFetchDefaultRoots={selectDBAndFetchDefaultRoots}
              isFileExcelExisting={isFileExcelExisting}
              isComponentInstance={isComponentInstance}
            />
          )}

          {isExcelSource() && (
            <ExcelAmountSourcePanel
              isEdit={isEdit}
              objectSource={objectSource}
              updateCoObjectSourceAmount={updateCoObjectSourceAmount}
              validateSource={validateExcelSource}
              isFileExcelExisting={isFileExcelExisting}
              fileExcelName={fileExcelName}
              updateFileExcelName={updateFileExcelName}
              isComponentInstance={isComponentInstance}
              isLocked={isLocked}
              originalSheetArea={props.originalSheetArea}
              setOriginalSheetArea={props.setOriginalSheetArea}
              setIsOldSheetOrAreaSelected={props.setIsOldSheetOrAreaSelected}
              setIsLoadingSheetAndArea={props.setIsLoadingSheetAndArea}
              setIsSheetChartAndAreaToHide={props.setIsSheetChartAndAreaToHide}
              isSheetChartAndAreaToHide={props.isSheetChartAndAreaToHide}
              objectSourceImage={objectSource?.elabResultType}
            />
          )}
        </div>
      </div>
    </>
  );
};
